import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  useLocation,
} from 'react-router';

import { ErrorPage } from '@pages/error-page';
import { useMeQuery } from '@api/api-hooks/auth/useMeQuery.ts';
import {
  API_ERROR_PAGE_EVENT,
  type ApiErrorPageEventDetail,
  clearStoredApiErrorPageEventDetail,
  getStoredApiErrorPageEventDetail,
} from '@api/response-errors.ts';
import { Path } from '@assets/enums';
import { useAuthContext } from '@assets/helpers/hooks';
import { Header, Loader, PublicRoutesLayout } from '@src/components';

import { lazyNamedPage, renderLazyPage } from './lazy-page';
import { privateRoutes } from './private-routes';

const LoginPage = lazyNamedPage(() => import('@pages/login/LoginPage.tsx'), 'LoginPage');
const DocumentViewerPage = lazyNamedPage(
  () => import('@pages/document-viewer/DocumentViewerPage.tsx'),
  'DocumentViewerPage'
);
const PasswordRecoverPage = lazyNamedPage(
  () => import('@pages/passord-recover/PasswordRecoverPage.tsx'),
  'PasswordRecoverPage'
);
const PasswordResetPage = lazyNamedPage(
  () => import('@pages/passord-reset/PasswordResetPage.tsx'),
  'PasswordResetPage'
);
const MailChangeStatusPage = lazyNamedPage(
  () => import('@pages/mail-change-status/MailChangeStatusPage.tsx'),
  'MailChangeStatusPage'
);
const InvitationPage = lazyNamedPage(
  () => import('@pages/invitation/InvitationPage.tsx'),
  'InvitationPage'
);

const publicRouteLoader = (
  <PublicRoutesLayout>
    <Loader size="large" />
  </PublicRoutesLayout>
);

// Public routes available to all users
const publicRoutes: RouteObject[] = [
  {
    path: Path.Login,
    element: renderLazyPage(<LoginPage />, publicRouteLoader),
  },
  {
    path: Path.Document,
    element: renderLazyPage(<DocumentViewerPage />, publicRouteLoader),
  },
  {
    path: Path.PasswordRecover,
    element: renderLazyPage(<PasswordRecoverPage />, publicRouteLoader),
  },
  {
    path: Path.PasswordReset,
    element: renderLazyPage(<PasswordResetPage />, publicRouteLoader),
  },
  {
    path: Path.MailChangeStatus,
    element: renderLazyPage(<MailChangeStatusPage />, publicRouteLoader),
  },
  {
    path: Path.Invitation,
    element: renderLazyPage(<InvitationPage />, publicRouteLoader),
  },
];

export const AuthChecker = () => {
  const { pathname } = useLocation();
  const isMailChangeStatusPath = pathname === Path.MailChangeStatus;
  const { isSuccess: isMeSuccess, isLoading: isIAmLoading } = useMeQuery({
    enabled: !isMailChangeStatusPath,
  });

  const { isAuth } = useAuthContext();

  // Show loader during initial data loading
  if (isIAmLoading) {
    return (
      <PublicRoutesLayout>
        <Loader size="large" />
      </PublicRoutesLayout>
    );
  }

  if (isMeSuccess && isAuth && pathname === Path.Login) {
    return <Navigate to={Path.Partners} replace />;
  }

  // Display the contents
  return <Outlet />;
};

type ApiErrorPageHandlerProps = {
  children: ReactNode;
};

const ApiErrorPageHandler = ({ children }: ApiErrorPageHandlerProps) => {
  const { key } = useLocation();
  const locationKeyRef = useRef(key);
  const [errorState, setErrorState] = useState<ApiErrorPageEventDetail | null>(
    getStoredApiErrorPageEventDetail
  );

  const clearErrorState = useCallback(() => {
    clearStoredApiErrorPageEventDetail();
    setErrorState(null);
  }, []);

  useEffect(() => {
    const handleApiErrorPage = (event: Event) => {
      const detail = (event as CustomEvent<ApiErrorPageEventDetail>).detail;

      setErrorState(detail);
    };

    window.addEventListener(API_ERROR_PAGE_EVENT, handleApiErrorPage);
    setErrorState(getStoredApiErrorPageEventDetail());

    return () => {
      window.removeEventListener(API_ERROR_PAGE_EVENT, handleApiErrorPage);
    };
  }, []);

  useEffect(() => {
    if (locationKeyRef.current === key) {
      return;
    }

    locationKeyRef.current = key;
    clearErrorState();
  }, [clearErrorState, key]);

  if (errorState) {
    return <ErrorPageElement errorState={errorState} onGoHome={clearErrorState} />;
  }

  return children;
};

const ErrorPageElement = ({
  errorState,
  onGoHome,
}: {
  errorState?: ApiErrorPageEventDetail;
  onGoHome?: () => void;
}) => (
  <Header hideOutlet>
    <ErrorPage errorState={errorState} onGoHome={onGoHome} />
  </Header>
);

const errorPageElement = <ErrorPageElement />;

const ProtectedRoutes = () => {
  const { isAuth } = useAuthContext();

  return isAuth ? <Outlet /> : <Navigate to={Path.Login} />;
};

export const router = createBrowserRouter([
  {
    element: (
      <ApiErrorPageHandler>
        <AuthChecker />
      </ApiErrorPageHandler>
    ),
    children: [
      {
        path: Path.ErrorPage,
        element: errorPageElement,
      },
      {
        element: <PublicRoutesLayout />,
        children: publicRoutes,
      },
      {
        element: <ProtectedRoutes />,
        children: [
          {
            element: <Header />,
            children: privateRoutes,
          },
        ],
      },
    ],
    errorElement: errorPageElement,
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
